import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowRight, Calculator, Check, ChevronDown, ClipboardCheck, FileText, FolderLock,
  HeartPulse, ListChecks, Scale,
} from "lucide-react";
import { Reveal } from "@/components/Reveal";
import SEO from "@/components/SEO";

const inputCls = "w-full min-h-[52px] rounded-xl border border-sand bg-white px-4 text-base text-navy focus:border-gold focus:ring-2 focus:ring-gold/30 outline-none transition";

export default function Tools() {
  const [open, setOpen] = useState(null);
  const toggle = (id) => setOpen((cur) => (cur === id ? null : id));

  return (
    <div className="bg-cream min-h-[80vh]">
      <SEO title="Legacy Planning Tools" description="Free educational tools: Legacy Checkup, will vs. trust education, life insurance needs estimator, beneficiary organizer, and document checklist." path="/tools" />
      <div className="max-w-3xl mx-auto px-5 py-12 md:py-16">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.28em] text-gold mb-4">Explore Tools</p>
          <h1 className="font-serif text-4xl sm:text-5xl font-semibold text-navy leading-tight">
            Gentle tools to help your family plan
          </h1>
          <p className="mt-4 text-lg text-slate-600 leading-relaxed">
            Educational resources you can use at your own pace. Nothing here is a recommendation — just clarity.
          </p>
        </Reveal>

        <div className="mt-10 space-y-4">
          <ToolShell icon={ClipboardCheck} title="Legacy Checkup™" desc="See how prepared your current legacy plan may be." testid="tool-card-legacy-checkup" open={open === "checkup"} onToggle={() => toggle("checkup")}>
            <p className="text-slate-600 leading-relaxed">
              Our flagship 3-minute assessment identifies which areas of your plan are strong and which may deserve
              a closer look — with a personalized report at the end.
            </p>
            <Link to="/checkup" data-testid="tool-start-checkup" className="mt-5 inline-flex items-center gap-2 min-h-[52px] rounded-full bg-navy text-cream font-bold text-sm tracking-widest px-7 hover:bg-navy-light transition-colors">
              START MY CHECKUP <ArrowRight className="w-4 h-4" />
            </Link>
          </ToolShell>

          <ToolShell icon={Scale} title="Will vs. Trust Education" desc="Learn the basic differences between wills and trusts." testid="tool-card-will-vs-trust" open={open === "willtrust"} onToggle={() => toggle("willtrust")}>
            <div className="overflow-x-auto -mx-1">
              <table className="w-full text-sm min-w-[420px]">
                <thead>
                  <tr className="text-left">
                    <th className="py-2 pr-3 text-slate-500 font-bold"></th>
                    <th className="py-2 pr-3 font-serif text-lg text-navy">Will</th>
                    <th className="py-2 font-serif text-lg text-navy">Living Trust</th>
                  </tr>
                </thead>
                <tbody className="text-slate-700">
                  {[
                    ["When it takes effect", "After passing away", "While you're living"],
                    ["Probate court process", "Usually required", "Often avoided for trust assets"],
                    ["Privacy", "Becomes public record", "Generally remains private"],
                    ["Names guardians for children", "Yes", "No (a will does this)"],
                    ["Helps if you're incapacitated", "No", "Yes, for trust assets"],
                  ].map(([label, will, trust]) => (
                    <tr key={label} className="border-t border-sand">
                      <td className="py-3 pr-3 font-semibold text-navy">{label}</td>
                      <td className="py-3 pr-3">{will}</td>
                      <td className="py-3">{trust}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-4 text-xs text-slate-500 leading-relaxed">
              Educational comparison only — this tool does not suggest which legal document is right for you. A
              qualified estate-planning attorney can advise on your situation.{" "}
              <Link to="/learn/will-vs-trust-basics" className="underline decoration-gold/60 underline-offset-2 font-semibold">Read: Will vs. Trust Basics</Link>
            </p>
          </ToolShell>

          <ToolShell icon={Calculator} title="Life Insurance Needs Estimator" desc="Estimate financial obligations your family may need help covering." testid="tool-card-insurance-calculator" open={open === "estimator"} onToggle={() => toggle("estimator")}>
            <Estimator />
          </ToolShell>

          <ToolShell icon={ListChecks} title="Beneficiary Organizer" desc="Create a checklist of accounts and beneficiary designations you may want to review." testid="tool-card-beneficiary-organizer" open={open === "beneficiaries"} onToggle={() => toggle("beneficiaries")}>
            <Checklist
              storageKey="lw_beneficiaries"
              items={[
                "Life insurance policies", "Employer retirement plans (401(k), 403(b))", "IRAs",
                "Bank accounts (payable-on-death)", "Investment & brokerage accounts", "Annuities",
                "Pension benefits", "Health savings accounts (HSA)",
              ]}
              note="Check off each account once you've confirmed the named beneficiaries are current. We never ask for account numbers or passwords — this list stays on your device."
            />
          </ToolShell>

          <ToolShell icon={FolderLock} title="Legacy Document Checklist" desc="See common documents families may want to organize." testid="tool-card-document-checklist" open={open === "documents"} onToggle={() => toggle("documents")}>
            <Checklist
              storageKey="lw_documents"
              items={[
                "Will", "Trust documents", "Life insurance policies", "Financial power of attorney",
                "Healthcare directive", "Property deeds", "Vehicle titles", "List of financial accounts (institutions only)",
                "Birth & marriage certificates", "Contact list for attorney / advisors",
              ]}
              note="Gather these into one secure place and tell someone you trust where they are. This checklist is saved only on your device."
            />
          </ToolShell>

          <div className="rounded-3xl border-2 border-dashed border-sand bg-white p-6 sm:p-8" data-testid="tool-card-medicare">
            <div className="flex items-start gap-4">
              <span className="w-12 h-12 shrink-0 rounded-2xl bg-cream-dark border border-sand flex items-center justify-center">
                <HeartPulse className="w-6 h-6 text-navy-light" />
              </span>
              <div>
                <h3 className="font-serif text-2xl font-semibold text-navy">Medicare Education</h3>
                <p className="mt-1 text-slate-600 leading-relaxed">Plain-English guidance for approaching Medicare decisions.</p>
              </div>
            </div>
            <button onClick={() => toggle("medicare")} className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-navy hover:text-gold transition-colors" data-testid="medicare-toggle">
              {open === "medicare" ? "Hide" : "Learn more"} <ChevronDown className={`w-4 h-4 transition-transform ${open === "medicare" ? "rotate-180" : ""}`} />
            </button>
            <AnimatePresence>
              {open === "medicare" && (
                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.35 }} className="overflow-hidden">
                  <div className="pt-5 space-y-3 text-slate-600 leading-relaxed text-sm sm:text-base">
                    <p>Medicare is the federal health insurance program primarily for people 65 and older. In general terms: hospital coverage, medical coverage, prescription drug coverage, and private-plan alternatives each play different roles. This page provides general education only — it does not describe or compare any specific plan.</p>
                    <p>Most people first enroll around age 65 during a 7-month initial enrollment window. Missing key windows can mean late enrollment penalties, so marking your calendar early is worthwhile.</p>
                    <p className="text-xs text-slate-500 border-t border-sand pt-3">
                      Medicare education is a separate educational track and is not connected to your Legacy Checkup
                      results — your checkup answers are never used for Medicare marketing. Simply visiting or reading
                      this page does not authorize anyone to market Medicare plans to you. If you affirmatively request
                      a Medicare conversation below, that request will be handled in line with applicable
                      permission-to-contact, Scope of Appointment, CMS, carrier, and TPMO requirements.
                    </p>
                    <p className="text-xs text-slate-400 italic">This Medicare content is pending final compliance review by Legacy Wave Consulting's licensed compliance professional.</p>
                    <Link to="/contact" className="inline-flex items-center gap-2 font-bold text-navy hover:text-gold transition-colors" data-testid="medicare-book-link">
                      Medicare questions? Request a conversation <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
}

function ToolShell({ icon: Icon, title, desc, testid, open, onToggle, children }) {
  return (
    <div className="rounded-3xl border border-sand bg-white p-6 sm:p-8 shadow-sm" data-testid={testid}>
      <div className="flex items-start gap-4">
        <span className="w-12 h-12 shrink-0 rounded-2xl bg-navy flex items-center justify-center">
          <Icon className="w-6 h-6 text-gold" />
        </span>
        <div>
          <h3 className="font-serif text-2xl font-semibold text-navy">{title}</h3>
          <p className="mt-1 text-slate-600 leading-relaxed">{desc}</p>
        </div>
      </div>
      <button onClick={onToggle} className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-navy hover:text-gold transition-colors">
        {open ? "Close" : "Open tool"} <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.35 }} className="overflow-hidden">
            <div className="pt-5 border-t border-sand mt-5">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function Estimator() {
  const [vals, setVals] = useState({ income: "", years: "10", mortgage: "", debts: "", finalExpenses: "15000", education: "", existing: "" });
  const [estimate, setEstimate] = useState(null);
  const num = (v) => Number(String(v).replace(/[^0-9.]/g, "")) || 0;

  const calculate = (e) => {
    e.preventDefault();
    const need = num(vals.income) * num(vals.years) + num(vals.mortgage) + num(vals.debts) + num(vals.finalExpenses) + num(vals.education) - num(vals.existing);
    setEstimate(Math.max(0, Math.round(need / 1000) * 1000));
  };

  const fields = [
    ["income", "Annual income to replace ($)"],
    ["years", "Years of income replacement"],
    ["mortgage", "Remaining mortgage ($)"],
    ["debts", "Other debts ($)"],
    ["finalExpenses", "Final expenses ($)"],
    ["education", "Education funding ($)"],
    ["existing", "Existing life insurance coverage ($)"],
  ];

  return (
    <form onSubmit={calculate} className="space-y-4" data-testid="estimator-form">
      <div className="grid sm:grid-cols-2 gap-4">
        {fields.map(([k, label]) => (
          <div key={k}>
            <label className="block text-sm font-bold text-navy mb-1.5" htmlFor={`est-${k}`}>{label}</label>
            <input id={`est-${k}`} inputMode="numeric" className={inputCls} value={vals[k]} onChange={(e) => setVals((v) => ({ ...v, [k]: e.target.value }))} placeholder="0" />
          </div>
        ))}
      </div>
      <button type="submit" data-testid="estimator-calculate" className="min-h-[52px] rounded-full bg-navy text-cream font-bold text-sm tracking-widest px-8 hover:bg-navy-light transition-colors">
        ESTIMATE MY NEEDS
      </button>
      {estimate !== null && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="bg-gold-light border border-sand rounded-2xl p-6 text-center" data-testid="estimator-result">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold">Estimated Coverage Need</p>
          <p className="mt-2 font-serif text-4xl font-semibold text-navy">${estimate.toLocaleString()}</p>
          <p className="mt-3 text-xs text-slate-500 leading-relaxed max-w-md mx-auto">
            This is a rough educational estimate only — not an insurance recommendation. Many factors affect actual
            needs; a licensed professional can help you review them.
          </p>
        </motion.div>
      )}
    </form>
  );
}

function Checklist({ items, note, storageKey }) {
  const [checked, setChecked] = useState(() => {
    try { return JSON.parse(localStorage.getItem(storageKey) || "[]"); } catch { return []; }
  });

  const toggle = (item) => {
    setChecked((cur) => {
      const next = cur.includes(item) ? cur.filter((i) => i !== item) : [...cur, item];
      localStorage.setItem(storageKey, JSON.stringify(next));
      return next;
    });
  };

  const pct = Math.round((checked.length / items.length) * 100);

  return (
    <div>
      <div className="flex items-center justify-between text-sm font-bold text-navy mb-2">
        <span>{checked.length} of {items.length} reviewed</span>
        <span className="text-gold">{pct}%</span>
      </div>
      <div className="h-2 bg-sand/60 rounded-full overflow-hidden mb-5">
        <div className="h-full bg-gold rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>
      <div className="space-y-2.5">
        {items.map((item) => {
          const on = checked.includes(item);
          return (
            <button key={item} onClick={() => toggle(item)} data-testid="checklist-item" className={`w-full min-h-[52px] text-left rounded-xl border-2 px-4 py-3 flex items-center gap-3 transition-colors ${on ? "border-gold bg-gold-light" : "border-sand bg-cream hover:border-gold/50"}`}>
              <span className={`w-6 h-6 shrink-0 rounded-md border-2 flex items-center justify-center ${on ? "border-gold bg-gold" : "border-sand bg-white"}`}>
                {on && <Check className="w-4 h-4 text-navy" strokeWidth={3} />}
              </span>
              <span className={`font-semibold ${on ? "text-navy" : "text-slate-700"}`}>{item}</span>
            </button>
          );
        })}
      </div>
      <p className="mt-4 text-xs text-slate-500 leading-relaxed">{note}</p>
    </div>
  );
}
